import { flexRender, Table as TanstackTable } from '@tanstack/react-table';
import {
  Button,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'component-library';
import { TFunction } from 'i18next';

import { DraftPlot, DraftPlotGroup } from '@/api/rest/resources/types/plot';

import { PAGE_SIZE } from '../constants';
import { DraftPlotGroupsTable } from './DraftPlotGroupsTable';

type CoordinatesReviewPanelProps = {
  t: TFunction;
  allDraftPlots: DraftPlot[];
  filteredPlots: DraftPlot[];
  groups: DraftPlotGroup[];
  hasUngroupedPlots: boolean;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  currentPage: number;
  setCurrentPage: (v: number) => void;
  pageStart: number;
  pageEnd: number;
  table: TanstackTable<DraftPlot>;
  isDiscarding: boolean;
  onUploadMore: () => void;
  onDiscardAll: () => void;
  onRowClick: (id: string) => void;
  onSelectGroup: (id: string) => void;
};

export const CoordinatesReviewPanel = ({
  t,
  allDraftPlots,
  filteredPlots,
  groups,
  hasUngroupedPlots,
  searchTerm,
  setSearchTerm,
  currentPage,
  setCurrentPage,
  pageStart,
  pageEnd,
  table,
  isDiscarding,
  onUploadMore,
  onDiscardAll,
  onRowClick,
  onSelectGroup,
}: CoordinatesReviewPanelProps) => (
  <Stack spacing={4}>
    <Stack direction='row' className='items-center justify-between'>
      <span className='typography-overline text-text-secondary'>
        {t('shared.uploadPlots.review.plotCount', { count: allDraftPlots.length })}
      </span>
      <Stack direction='row' className='gap-2'>
        <Button variant='outline' size='small' onClick={onUploadMore}>
          {t('shared.uploadPlots.review.uploadMore')}
        </Button>
        <Button variant='outline' size='small' loading={isDiscarding} onClick={onDiscardAll}>
          {t('shared.uploadPlots.review.discardAll')}
        </Button>
      </Stack>
    </Stack>

    <input
      type='text'
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      placeholder={t('shared.uploadPlots.review.searchPlaceholder')}
      className='w-full rounded-lg border border-neutral-black-23 px-3 py-2 text-sm outline-none focus:border-primary-100'
    />

    <DraftPlotGroupsTable groups={groups} onSelect={onSelectGroup} />

    {hasUngroupedPlots && (
      <>
        <Stack className='flex-row items-center justify-between'>
          <span className='typography-overline text-text-secondary'>
            {t('shared.uploadPlots.review.paginationTitle', {
              count: filteredPlots.length,
              start: Math.max(1, pageStart + 1),
              end: Math.min(pageEnd, filteredPlots.length),
            })}
          </span>
          <Pagination
            className='typography-overline text-text-secondary'
            currentPage={currentPage}
            totalCount={filteredPlots.length}
            pageSize={PAGE_SIZE}
            onPageChange={(nextPage) => {
              table.setPageIndex(nextPage - 1);
              setCurrentPage(nextPage);
            }}
          />
        </Stack>

        <div className='rounded-3xl bg-white-100'>
          <Table data-testid='draft-plots-table'>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((h) => (
                    <TableHead key={h.id}>{flexRender(h.column.columnDef.header, h.getContext())}</TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-testid='draft-plot-row'
                  className='group/row cursor-pointer'
                  onClick={() => onRowClick(row.original.id)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      // The select checkbox is small; stop the click from bubbling to the row so a
                      // near-miss click toggles selection instead of accidentally opening the edit panel.
                      onClick={cell.column.id === 'select' ? (e) => e.stopPropagation() : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </>
    )}
  </Stack>
);
